import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { Attribution, withTransparentErrorBoundary } from '@confluence/error-boundary';
import { fg } from '@confluence/feature-gating';
import { keys } from '@confluence/storage-manager';

import { BaseAutomationDiscoveryDialog } from './BaseAutomationDiscoveryDialog';
import { EventSourceName } from './utils/analytics';
import { TimeSavedMessage } from './productivity-insights/TimeSavedMessage';

const i18n = defineMessages({
	globalPublicLinkTurnedOnHeader: {
		id: 'automation-discovery.global-public-link.public-link-turned-on.header',
		defaultMessage: 'Public links are now allowed on this site',
		description: 'A header telling the user public links were turned on at a global level',
	},
	globalPublicLinkTurnedOffHeader: {
		id: 'automation-discovery.global-public-link.public-link-turned-off.header',
		defaultMessage: 'Public links are no longer allowed on this site',
		description: 'A header telling the user public links were turned off at a global level',
	},
	ctaText: {
		id: 'automation-discovery.global-public-link.cta.text',
		defaultMessage: 'Auto-block public links for all new spaces?',
		description:
			'A header asking the user if they want to automatically block public links for all new spaces',
	},
});

type GlobalPublicLinkAutomationDiscoveryDialogProps = {
	onClose: () => void;
	isPublicLinksToggleOn: boolean;
};

const GlobalPublicLinkAutomationDiscoveryDialogComponent = ({
	onClose,
	isPublicLinksToggleOn,
}: GlobalPublicLinkAutomationDiscoveryDialogProps) => {
	const header = isPublicLinksToggleOn ? (
		<FormattedMessage {...i18n.globalPublicLinkTurnedOnHeader} />
	) : (
		<FormattedMessage {...i18n.globalPublicLinkTurnedOffHeader} />
	);

	const PromptInsightMessage = fg('a4c_insights_for_contextual_prompts') ? (
		<TimeSavedMessage timeSaved="343" />
	) : undefined;

	return (
		<BaseAutomationDiscoveryDialog
			eventSourceName={EventSourceName.GLOBAL_PUBLIC_LINKS}
			templateName="scheduledInactivePagesDisablePublicLink"
			discoveryExperience="automationDiscoveryGlobalPublicLink"
			onClose={onClose}
			cta={<FormattedMessage {...i18n.ctaText} />}
			insightMessage={PromptInsightMessage}
			header={header}
			localStorageKey={keys.AUTOMATION_DISCOVER_GLOBAL_PUBLIC_LINK}
			templateUrl="/wiki/admin/automation#/rule/template?templateId=confluence_collaborate_template_12"
		/>
	);
};

export const GlobalPublicLinkAutomationDiscoveryDialog = withTransparentErrorBoundary({
	attribution: Attribution.AUTOMATION_FOR_CONFLUENCE,
})(GlobalPublicLinkAutomationDiscoveryDialogComponent);
